import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { Button } from '../../ui/Button.tsx'
import { IconChevronDownOutlineRegular } from '../../ui/icons.tsx'
import { Menu } from '../../ui/Menu.tsx'
import { Switch } from '../../ui/Switch.tsx'
import type { FieldDraft } from '../../data/workspace-drafts.ts'
import { ConfirmDialog } from '../../ui/ConfirmDialog.tsx'
import { FormField } from '../../ui/FormField.tsx'
import { HintTooltip } from '../../ui/HintTooltip.tsx'
import { MenuSelect } from '../../ui/MenuSelect.tsx'
import type { PromptToolTranslate } from '../../locales.ts'
import { patchToolParameter } from './custom-tool-parameters.ts'
import sharedCss from '../../ui/controls.module.css'
import featureCss from './tools.module.css'

const styles = { ...sharedCss, ...featureCss }
/** 内置工具名（delegate 提示）。 */
const BUILTIN_TOOL_NAMES = ['character_list', 'character_import', 'character_apply', 'character_remove', 'character_delete',
  'world_book_list', 'world_book_upsert', 'world_book_delete', 'session_var']

/**
 * execute.kind 下拉取值：必须与 engine/tool-definition.mjs:79 的校验列表逐值一致
 * （对拍守卫见 test/client/mirror-guards.test.mjs，那里 import 引擎模块做行为对拍）。
 */
export const KIND_OPTIONS = ['shell', 'http', 'delegate', 'fs', 'ask-user'] as const
/** fs 的 action 取值：与 engine/tool-definition.mjs:92 的 read/write/append/list/delete 同域（引擎另收 {{args.*}} 模板，UI 不提供）。 */
export const FS_ACTIONS = ['read', 'write', 'append', 'list', 'delete'] as const
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] as const
const SHELLS = ['pwsh', 'powershell', 'cmd', 'sh', 'bash'] as const
/**
 * parameters 行的 type 下拉：前 7 项 = engine/tool-definition.mjs:4 `JSON_SCHEMA_TYPES`（物化后的类型集）。
 * 末两项 `json` / `oneOf` 是 **UI 专有**的作者侧形态：引擎只校验物化结果，`type: 'json'` / `type: 'oneOf'`
 * 会被直接拒绝（tool-definition.mjs:17 的报错即为此而写）；二者由 host 保存前的
 * parameterSchemaSpecToJsonSchema 物化——`json` → 仅注释节点（任意 JSON），`oneOf` → 分支联合。
 * 因此行编辑器必须把「无 type 的节点」显示为 json、把 oneOf 节点显示为 oneOf（见下方 type 推导），
 * 不能把这两个值当成引擎类型。对拍守卫见 test/client/mirror-guards.test.mjs。
 */
export const SCHEMA_TYPES = ['string', 'number', 'integer', 'boolean', 'null', 'array', 'object', 'json', 'oneOf'] as const

export type ToolDraft = Record<string, unknown>

export function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

/** parameters 行式编辑：key + type 下拉 + required 开关 + description，可增删。 */
function ParameterRowsEditor(props: { t: PromptToolTranslate; value: ToolDraft | undefined; onChange: (value: ToolDraft | undefined) => void }): ReactNode {
  const { t } = props
  const params = asRecord(props.value)
  const rows = Object.entries(params)
  const commit = (next: Array<[string, unknown]>): void => {
    props.onChange(next.length > 0 ? Object.fromEntries(next) : undefined)
  }
  const setRow = (index: number, patch: Partial<{ key: string; type: string; required: boolean; description: string }>): void => {
    const { key, ...changes } = patch
    commit(rows.map(([name, spec], at) => at === index ? [key ?? name, patchToolParameter(asRecord(spec), changes)] : [name, spec]))
  }
  return (
    <span className={styles.configFieldStack}>
      <span className={styles.configFieldLabel}>{t('toolEditor.params.label')}</span>
      {rows.length === 0 && <p className={styles.configFieldHint}>{t('toolEditor.params.empty')}</p>}
      {rows.map(([key, spec], index) => {
        const record = asRecord(spec)
        const type = typeof record.type === 'string' ? record.type : Array.isArray(record.oneOf) ? 'oneOf' : 'json'
        return <span key={index} className={styles.variableRow}>
          <input className={styles.configInput} aria-label={t('toolEditor.params.nameAria')} value={key} spellCheck={false} placeholder={t('toolEditor.params.namePlaceholder')}
            onChange={(e) => setRow(index, { key: e.target.value })} />
          <MenuSelect className={styles.configInput} compact ariaLabel={t('toolEditor.params.typeAria')} value={type}
            options={SCHEMA_TYPES.map((type) => ({ value: type, label: type }))}
            onChange={(type) => setRow(index, { type })} />
          <HintTooltip label={t('toolEditor.params.requiredHint')}>
            <Switch label={t('toolEditor.params.requiredAria')} checked={record.required === true}
              onChange={(checked) => setRow(index, { required: checked })} />
          </HintTooltip>
          <input className={styles.configInput} aria-label={t('toolEditor.params.descriptionAria')} value={typeof record.description === 'string' ? record.description : ''} spellCheck={false} placeholder={t('toolEditor.params.descriptionPlaceholder')}
            onChange={(e) => setRow(index, { description: e.target.value })} />
          <button type="button" className={styles.pillButton} data-danger aria-label={t('toolEditor.params.removeAria', { key: key || index })}
            onClick={() => commit(rows.filter((_, at) => at !== index))}>{t('toolEditor.params.remove')}</button>
        </span>
      })}
      <span>
        <button type="button" className={styles.pillButton} onClick={() => commit([...rows, ['', { type: 'string' }]])}>
          {t('toolEditor.params.add')}
        </button>
      </span>
    </span>
  )
}

/** 本 feature 内的 JSON 草稿：非法中间态不回弹，失焦后只提交对象。 */
function ToolJsonField(props: { t: PromptToolTranslate; label: string; value: ToolDraft; drafts?: Map<string, FieldDraft>; draftKey: string; onChange: (value: ToolDraft) => void }): ReactNode {
  const { t } = props
  const serialized = JSON.stringify(props.value, null, 2)
  const retained = props.drafts?.get(props.draftKey)
  const [text, setText] = useState(retained?.text ?? serialized)
  const [error, setError] = useState(retained?.error ?? '')
  const errorId = useId()
  const remember = (nextText: string, nextError: string, source = serialized): void => {
    props.drafts?.set(props.draftKey, { source, text: nextText, error: nextError })
    setText(nextText); setError(nextError)
  }
  useEffect(() => {
    const field = props.drafts?.get(props.draftKey)
    if (field !== undefined && field.text !== field.source) return
    remember(serialized, '')
  }, [serialized, props.draftKey])
  return <FormField label={props.label} hint={t('toolEditor.json.hint')}>
    <textarea className={styles.configTextarea} rows={5} aria-label={props.label} aria-invalid={error.length > 0}
      aria-describedby={error ? errorId : undefined}
      value={text} spellCheck={false} onChange={(event) => remember(event.target.value, '')}
      onBlur={() => {
        try {
          const value: unknown = JSON.parse(text.trim() || '{}')
          if (value === null || typeof value !== 'object' || Array.isArray(value)) { remember(text, t('toolEditor.json.mustBeObject')); return }
          const accepted = JSON.stringify(value, null, 2)
          remember(accepted, '', accepted)
          props.onChange(value as ToolDraft)
        } catch { remember(text, t('toolEditor.json.invalid')) }
      }} />
    {error && <small id={errorId} role="alert">{error}</small>}
  </FormField>
}

/** 单张工具卡片（对齐模块列表卡片形态）：header（enabled 开关 + chips + 上移/下移/复制/两段式删除）+ Field 表单。 */
export function CustomToolCard(props: {
  t: PromptToolTranslate
  tool: ToolDraft
  fieldDrafts?: Map<string, FieldDraft>
  index: number
  expanded: boolean
  /** 只读（system 模块或关闭 writePreset）：禁掉写操作，但保留卡片展开/折叠。 */
  disabled?: boolean
  onToggleExpanded: () => void
  onPatch: (patch: Partial<ToolDraft>) => void
  onToggleEnabled: (enabled: boolean) => void
  onMoveUp: () => void
  onMoveDown: () => void
  onDuplicate: () => void
  onRemove: () => void
  canMoveUp: boolean
  canMoveDown: boolean
}): ReactNode {
  const { tool, index, t } = props
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const actionRef = useRef<HTMLSpanElement>(null)
  const focusAction = (): void => { actionRef.current?.querySelector('button')?.focus() }
  const bodyId = useId()
  const timeoutKey = `${String(tool.id ?? index)}:timeout`
  const timeoutRetained = props.fieldDrafts?.get(timeoutKey)
  const [timeoutDraft, setTimeoutDraft] = useState<string | undefined>(timeoutRetained?.text)
  const [timeoutError, setTimeoutError] = useState(timeoutRetained?.error ?? '')
  const timeoutErrorId = useId()
  useEffect(() => { if (props.disabled) { setMenuOpen(false); setConfirmingDelete(false) } }, [props.disabled])
  const execute = asRecord(tool.execute)
  const kind = typeof execute.kind === 'string' ? execute.kind : 'shell'
  const name = typeof tool.name === 'string' ? tool.name : ''
  const id = typeof tool.id === 'string' ? tool.id : `tool-${index + 1}`
  const description = typeof tool.description === 'string' ? tool.description : ''
  const enabled = tool.enabled !== false
  const paramCount = Object.keys(asRecord(tool.parameters)).length
  const chips = [kind]
  if (paramCount > 0) chips.push(t('toolEditor.chip.params', { count: paramCount }))
  if (Number.isSafeInteger(tool.timeoutMs) && (tool.timeoutMs as number) > 0) chips.push(`timeout=${tool.timeoutMs}`)
  const patchExecute = (patch: Record<string, unknown>): void => {
    props.onPatch({ execute: { ...execute, ...patch } })
  }
  return (
    <article className={clsx(styles.configCard, styles.toolCard, props.expanded && styles.configCardOpen)} data-tool-card="true">
      <header className={styles.configHeader}>
        <button type="button" className={styles.configToggle} aria-expanded={props.expanded} aria-controls={bodyId} onClick={props.onToggleExpanded}>
          <span className={styles.configTitle}>
            <span className={styles.configTitleRow}>
              <span className={styles.configName}>{name.length > 0 ? `${id} · ${name}` : id}</span>
              <span className={styles.configChip}>{kind}</span>
            </span>
            <span className={styles.configMeta}>{description || t('tools.surface.detail.noDescription')}{chips.length > 1 && ` · ${chips.slice(1).join(' · ')}`}</span>
          </span>
          <IconChevronDownOutlineRegular className={clsx(styles.chevron, props.expanded && styles.chevronOpen)} />
        </button>
        <div className={styles.configHeaderActions}>
          <fieldset className={styles.cardScopeActions} disabled={props.disabled === true}>
            <HintTooltip label={enabled ? t('toolEditor.enable.hint.on') : t('toolEditor.enable.hint.off')}>
              <Switch label={t('toolEditor.enable.aria', { id })} checked={enabled} disabled={props.disabled}
                onChange={props.onToggleEnabled} />
            </HintTooltip>
            <span ref={actionRef} tabIndex={-1}><Menu open={menuOpen && !props.disabled} compact
              anchor={<Button size="sm" variant="ghost" aria-label={t('card.actionsAria', { name: name || id })}
                aria-haspopup="menu" aria-expanded={menuOpen} disabled={props.disabled} onClick={() => setMenuOpen((open) => !open)}>⋯</Button>}
              onClose={() => setMenuOpen(false)}
              items={[
                { id: 'up', label: t('toolEditor.moveUp'), disabled: !props.canMoveUp },
                { id: 'down', label: t('toolEditor.moveDown'), disabled: !props.canMoveDown },
                { id: 'copy', label: t('toolEditor.duplicate') },
                { id: 'delete', label: t('toolEditor.remove'), danger: true },
              ]}
              onSelect={(action) => {
                setMenuOpen(false)
                focusAction()
                if (action === 'up') props.onMoveUp()
                else if (action === 'down') props.onMoveDown()
                else if (action === 'copy') props.onDuplicate()
                else setConfirmingDelete(true)
              }} /></span>
          </fieldset>
        </div>
      </header>
      {confirmingDelete && <ConfirmDialog title={t('card.deleteTitle', { name: name || id })}
        description={t('card.deleteDescription', { name: name || id })}
        confirmLabel={t('toolEditor.confirmRemove')} cancelLabel={t('toolEditor.cancel')}
        returnFocusRef={actionRef}
        onConfirm={props.onRemove} onCancel={() => setConfirmingDelete(false)} />}
      <div id={bodyId} hidden={!props.expanded}>
      {props.expanded && (
        <fieldset className={styles.configForm} disabled={props.disabled === true}>
          <span className={styles.variableRow}>
            <FormField label={t('toolEditor.field.id')}>
              <input className={styles.configInput} aria-label={t('toolEditor.field.idAria')} value={id} spellCheck={false}
                onChange={(e) => props.onPatch({ id: e.target.value })} />
            </FormField>
            <FormField label={t('toolEditor.field.name')}>
              <input className={styles.configInput} aria-label={t('toolEditor.field.nameAria')} value={name} spellCheck={false} placeholder="my_tool"
                onChange={(e) => props.onPatch({ name: e.target.value })} />
            </FormField>
          </span>
          <FormField label={t('toolEditor.field.description')}>
            <textarea className={styles.configTextarea} rows={2} aria-label={t('toolEditor.field.descriptionAria')} value={description} spellCheck={false}
              placeholder={t('toolEditor.field.descriptionPlaceholder')}
              onChange={(e) => props.onPatch({ description: e.target.value })} />
          </FormField>
          <FormField label={t('toolEditor.field.kind')} hint={t('toolEditor.field.kind.hint')}>
            <MenuSelect className={styles.configInput} compact ariaLabel={t('toolEditor.field.kindAria')} value={kind}
              options={KIND_OPTIONS.map((option) => ({ value: option, label: option }))}
              onChange={(value) => patchExecute({ kind: value, ...(value === 'fs' && execute.action === undefined ? { action: 'read' } : {}) })} />
          </FormField>
          {kind === 'shell' && (
            <>
              <FormField label="command" hint={t('toolEditor.field.command.hint')}>
                <textarea className={styles.configTextarea} rows={3} aria-label={t('toolEditor.field.commandAria')} spellCheck={false}
                  value={typeof execute.command === 'string' ? execute.command : ''} placeholder="Write-Output {{args.x}}"
                  onChange={(e) => patchExecute({ command: e.target.value })} />
              </FormField>
              <FormField label="shell" hint={t('toolEditor.field.shell.hint')}>
                <MenuSelect className={styles.configInput} compact ariaLabel="shell"
                  value={typeof execute.shell === 'string' ? execute.shell : 'pwsh'}
                  options={SHELLS.map((shell) => ({ value: shell, label: shell }))}
                  onChange={(value) => patchExecute({ shell: value })} />
              </FormField>
            </>
          )}
          {kind === 'http' && (
            <>
              <FormField label="url" hint={t('toolEditor.field.url.hint')}>
                <input className={styles.configInput} aria-label={t('toolEditor.field.urlAria')} spellCheck={false}
                  value={typeof execute.url === 'string' ? execute.url : ''} placeholder="https://…/{{args.q}}"
                  onChange={(e) => patchExecute({ url: e.target.value })} />
              </FormField>
              <FormField label="method">
                <MenuSelect className={styles.configInput} compact ariaLabel={t('toolEditor.field.methodAria')}
                  value={typeof execute.method === 'string' ? execute.method : 'GET'}
                  options={HTTP_METHODS.map((method) => ({ value: method, label: method }))}
                  onChange={(value) => patchExecute({ method: value })} />
              </FormField>
            </>
          )}
          {kind === 'delegate' && (
            <FormField label={t('toolEditor.field.delegate')} hint={t('toolEditor.field.delegate.hint', { names: BUILTIN_TOOL_NAMES.join(' / ') })}>
              <input className={styles.configInput} aria-label={t('toolEditor.field.delegateAria')} spellCheck={false}
                value={typeof execute.tool === 'string' ? execute.tool : ''} placeholder="world_book_upsert"
                onChange={(e) => patchExecute({ tool: e.target.value })} />
            </FormField>
          )}
          {kind === 'fs' && (
            <>
              <FormField label="action">
                <MenuSelect className={styles.configInput} compact ariaLabel={t('toolEditor.field.fsActionAria')}
                  value={typeof execute.action === 'string' && (FS_ACTIONS as readonly string[]).includes(execute.action) ? execute.action : 'read'}
                  options={FS_ACTIONS.map((action) => ({ value: action, label: action }))}
                  onChange={(value) => patchExecute({ action: value })} />
              </FormField>
              <FormField label="path" hint={t('toolEditor.field.path.hint')}>
                <input className={styles.configInput} aria-label={t('toolEditor.field.pathAria')} spellCheck={false}
                  value={typeof execute.path === 'string' ? execute.path : ''} placeholder="data/{{args.name}}.json"
                  onChange={(e) => patchExecute({ path: e.target.value })} />
              </FormField>
              {(execute.action === 'write' || execute.action === 'append' || String(execute.action).includes('{{args.')) && (
                <FormField label="content" hint={t('toolEditor.field.content.hint')}>
                  <textarea className={styles.configTextarea} rows={4} aria-label={t('toolEditor.field.contentAria')} spellCheck={false}
                    value={typeof execute.content === 'string' ? execute.content : ''}
                    onChange={(event) => patchExecute({ content: event.target.value })} />
                </FormField>
              )}
            </>
          )}
          {kind === 'ask-user' && (
            <FormField label={t('toolEditor.field.question')}>
              <input className={styles.configInput} aria-label={t('toolEditor.field.questionAria')} spellCheck={false}
                value={typeof execute.question === 'string' ? execute.question : ''} placeholder={t('toolEditor.field.questionPlaceholder')}
                onChange={(e) => patchExecute({ question: e.target.value })} />
            </FormField>
          )}
          <ParameterRowsEditor
            t={t}
            value={asRecord(tool.parameters)}
            onChange={(next) => props.onPatch({ parameters: next })}
          />
          <ToolJsonField t={t} drafts={props.fieldDrafts} draftKey={`${id}:parameters`} label={t('toolEditor.json.advanced')} value={asRecord(tool.parameters)} onChange={(parameters) => props.onPatch({ parameters })} />
          <ToolJsonField t={t} drafts={props.fieldDrafts} draftKey={`${id}:output`} label={t('toolEditor.json.output')} value={asRecord(tool.output)} onChange={(output) => props.onPatch({
            output: Object.keys(output).length > 0 ? output : { schema: { type: 'object', additionalProperties: true } },
          })} />
          <FormField label="timeoutMs" hint={t('toolEditor.timeout.hint')}>
            <input className={styles.configInput} type="text" inputMode="decimal" aria-label={t('toolEditor.timeoutAria')}
              aria-invalid={timeoutError.length > 0} aria-describedby={timeoutError ? timeoutErrorId : undefined} value={timeoutDraft ?? String(tool.timeoutMs ?? '')}
              onChange={(event) => { setTimeoutDraft(event.target.value); setTimeoutError(''); props.fieldDrafts?.set(timeoutKey, { source: String(tool.timeoutMs ?? ''), text: event.target.value, error: '' }) }}
              onBlur={() => {
                if (timeoutDraft === undefined) return
                const timeoutMs = timeoutDraft.trim() === '' ? undefined : Number(timeoutDraft)
                if (timeoutMs !== undefined && (!Number.isSafeInteger(timeoutMs) || timeoutMs <= 0 || timeoutMs > 2_147_483_647)) {
                  const error = t('toolEditor.timeout.invalid')
                  setTimeoutError(error)
                  props.fieldDrafts?.set(timeoutKey, { source: String(tool.timeoutMs ?? ''), text: timeoutDraft, error })
                  return
                }
                props.onPatch({ timeoutMs })
                props.fieldDrafts?.delete(timeoutKey)
                setTimeoutDraft(undefined)
              }} />
            {timeoutError && <small id={timeoutErrorId} role="alert">{timeoutError}</small>}
          </FormField>
        </fieldset>
      )}
      </div>
    </article>
  )
}
